import React from 'react';
import { meta } from '@/lib/lifecycle';
import { AlertTriangle, Clock, Star, Home, Video, RefreshCw } from 'lucide-react';
import { CAL } from '@/constants/testIds';

const now = () => new Date();

export default function DailyOperationsPanel({ appointments, lookups, metrics, onSelectAppointment }) {
  const t = now();
  const waiting = appointments.filter((a) => ['arrived', 'en_route'].includes(a.state));
  const delayed = appointments.filter((a) => {
    const s = new Date(a.start_ts);
    return s < t && ['created', 'assigned', 'reminder_sent', 'confirmed'].includes(a.state);
  });
  const highRisk = appointments.filter((a) => a.priority === 'vip' || a.priority === 'emergency');
  const homeVisits = appointments.filter((a) => a.channel === 'home_visit');
  const followups = appointments.filter((a) => a.state === 'followup_scheduled');
  const unconfirmed = appointments.filter((a) => ['created', 'assigned', 'reminder_sent'].includes(a.state) && new Date(a.start_ts) > t);

  const groups = [
    { title: 'Waiting patients', items: waiting, icon: Clock, tone: 'warning' },
    { title: 'Delayed', items: delayed, icon: AlertTriangle, tone: 'danger' },
    { title: 'High-risk / VIP', items: highRisk, icon: Star, tone: 'info' },
    { title: 'Unconfirmed upcoming', items: unconfirmed, icon: Clock, tone: 'default' },
    { title: 'Home visits', items: homeVisits, icon: Home, tone: 'default' },
    { title: 'Follow-ups due', items: followups, icon: RefreshCw, tone: 'default' },
  ];

  return (
    <aside
      className="w-[300px] shrink-0 border-l border-stone-200 bg-stone-50 overflow-auto hc-scrollbar"
      data-testid={CAL.dailyOpsPanel}
    >
      <div className="p-3 border-b border-stone-200 bg-white">
        <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Daily Operations</div>
        <div className="font-heading text-lg font-bold text-stone-900 mt-0.5">Today at a glance</div>
        <div className="grid grid-cols-3 gap-1 mt-3">
          <MiniStat label="Free slots" value={metrics ? Math.max(0, 24 - (metrics.total_today || 0)) : '—'} />
          <MiniStat label="Waiting" value={metrics?.waiting} />
          <MiniStat label="Home vis." value={metrics?.home_visits} />
        </div>
      </div>
      <div className="p-3 space-y-4">
        {groups.map((g) => (
          <div key={g.title}>
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-stone-500 font-semibold mb-1.5">
              <g.icon className="w-3.5 h-3.5" /> {g.title}
              <span className="ml-auto bg-stone-200 text-stone-700 px-1.5 py-0.5 rounded text-[10px] font-bold tabular-nums">
                {g.items.length}
              </span>
            </div>
            {g.items.length === 0 ? (
              <div className="text-xs text-stone-400 italic pl-4">None</div>
            ) : (
              <div className="space-y-1">
                {g.items.slice(0, 5).map((apt) => (
                  <button
                    key={apt.id}
                    onClick={() => onSelectAppointment(apt.id)}
                    className="w-full text-left bg-white border border-stone-200 rounded px-2 py-1.5 hover:bg-stone-50 flex items-center gap-2"
                  >
                    <div
                      className="w-1 h-8 rounded-full shrink-0"
                      style={{ backgroundColor: meta(apt.state).text }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-stone-900 truncate">
                        {lookups.patient[apt.patient_id]?.full_name || apt.patient_name || 'Patient'}
                        {apt.priority === 'vip' && <Star className="inline w-3 h-3 ml-1 fill-current text-amber-500" />}
                        {apt.channel === 'home_visit' && <Home className="inline w-3 h-3 ml-1 text-stone-600" />}
                        {apt.channel === 'teleconsultation' && <Video className="inline w-3 h-3 ml-1 text-stone-600" />}
                      </div>
                      <div className="text-[10px] text-stone-500 truncate">
                        {new Date(apt.start_ts).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })} ·{' '}
                        {lookups.aptType[apt.appointment_type_id]?.name || '—'}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </aside>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="border border-stone-200 rounded p-1.5 bg-white text-center">
      <div className="font-heading font-bold text-lg tabular-nums text-stone-900">{value ?? '—'}</div>
      <div className="text-[9px] uppercase tracking-wider text-stone-500">{label}</div>
    </div>
  );
}
